// MCIP-14 — the fix card. One developer-shaped cluster in, everything a developer
// needs to act: evidence, projection, the copy-ready PR prompt, next steps, and the
// Slack/Jira dispatch buttons behind a confirm dialog.
//
// Two rules carried over from the rest of the app:
//  - Evidence rows render exactly what the API returned; a degraded connector's row
//    is absent and NAMED in `missing_evidence`, never rendered blank.
//  - The PR prompt is displayed verbatim from the stored record. The UI never edits
//    it — the same run+cluster must regenerate the identical prompt (ticket AC).
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api } from '../lib/api';
import FeedbackBlock from './FeedbackBlock';
import { pollAnalysis } from '../lib/aiPoll';
import { pct, signedPts } from '../lib/format';

function CopyButton({ text }) {
  const [state, setState] = useState('idle'); // idle | copied | failed
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setState('copied');
    } catch {
      setState('failed');
    }
    setTimeout(() => setState('idle'), 1600);
  }
  return (
    <button type="button" className="ghost-btn" onClick={copy} aria-live="polite">
      {state === 'copied' ? 'Copied ✓' : state === 'failed' ? 'Select & copy manually' : 'Copy prompt'}
    </button>
  );
}

export default function FixCard({ cluster }) {
  const [showPrompt, setShowPrompt] = useState(false); // PR prompt collapsed by default
  const [aiState, setAiState] = useState('idle'); // idle | loading | done | error
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState(null);

  // Analysis is MANUAL: a fresh Glean run only starts when the user clicks the
  // button. But the run belongs to the SERVER, not to this component — closing the
  // card mid-flight must not lose it. On open we probe (free): cached → render
  // instantly; running → show the spinner and poll until the cache fills.
  useEffect(() => {
    let live = true;
    let timer = null;
    setAiState('idle'); setAiError(null); setAiResult(null);

    const loadCached = () =>
      api.aiAnalysis(cluster.cluster_id) // cache hit — instant, no spend
        .then((r) => { if (live) { setAiResult(r); setAiState('done'); } });

    let wasWatching = false;
    const watch = async () => {
      try {
        const p = await api.aiStatus(cluster.cluster_id);
        if (!live) return;
        if (p.cached) { await loadCached(); return; }
        if (p.running) {
          wasWatching = true;
          setAiState('loading'); // resumed: an analysis started earlier is still going
          timer = setTimeout(watch, 5000);
          return;
        }
        // Neither cached nor running: only an error if we were watching a run —
        // a fresh idle card just keeps its button.
        if (wasWatching) {
          setAiError(p.error || { message: 'the background analysis did not complete — re-run it' });
          setAiState('error');
        }
      } catch { /* probe is best-effort; the button remains */ }
    };

    watch();
    return () => { live = false; if (timer) clearTimeout(timer); };
  }, [cluster.cluster_id]);
  const proj = cluster.projection || {};

  return (
    <div className="fixcard">
      {proj.projected_pass_rate !== null && proj.projected_pass_rate !== undefined && (
        <div className="projection">
          <span className="projection__label">If fixed</span>
          <span className="projection__value">{pct(proj.base_pass_rate)} → {pct(proj.projected_pass_rate)}</span>
          <span className="projection__delta is-good">{signedPts(proj.delta_pts)}</span>
          <span className="projection__foot">unblocks {proj.unblocks} tests</span>
        </div>
      )}

      <h3 className="detail__h3">Evidence</h3>
      <dl className="evidence">
        {cluster.evidence?.map((row) => (
          <div className="evidence__row" key={row.key}>
            <dt>{row.label}</dt>
            <dd>
              {row.url ? (
                // The CI Visibility link is a long explorer URL — render it as
                // a named chip (it sits right under Trace); other URLs (trace,
                // runbook) stay verbatim so they can be eyeballed before use.
                row.key === 'ci_test' ? (
                  <a className="tag" href={row.url} target="_blank" rel="noreferrer">Datadog test runs ↗</a>
                ) : (
                  <a href={row.url} target="_blank" rel="noreferrer">{row.url}</a>
                )
              ) : (
                <>
                  {row.detail && <span className="muted">{row.detail}: </span>}
                  {row.value}
                  {row.count > 1 && <span className="muted"> ×{row.count}</span>}
                </>
              )}
            </dd>
          </div>
        ))}
      </dl>
      {cluster.missing_evidence?.length > 0 && (
        <p className="detail__missing">
          Not available: {cluster.missing_evidence.join(', ')}
          {cluster.degraded_connectors?.length > 0 && <> — degraded: {cluster.degraded_connectors.join(', ')}</>}
        </p>
      )}

      {/* Glean analysis: the SAME stored evidence, consumed by Glean instead of a
          human paste. Cited or honest about being unavailable — never a bare guess. */}
      <div className="prompt__head">
        <FeedbackBlock cluster={cluster} />
        <h3 className="detail__h3">AI analysis — Glean, grounded in Motive code &amp; runbooks</h3>
        <button
          type="button"
          className="primary-btn"
          disabled={aiState === 'loading'}
          onClick={async () => {
            setAiState('loading'); setAiError(null);
            try {
              // First click uses the cache-or-run path; once a result is shown,
              // the button re-runs fresh (force). Uncached runs come back as
              // 202 { running: true } — pollAnalysis rides the probe until the
              // server-side Glean run lands (async-poll contract; the request
              // itself must finish well inside the ingress timeout).
              const force = aiState === 'done';
              const result = await pollAnalysis({
                start: () => api.aiAnalysis(cluster.cluster_id, { force }),
                probe: () => api.aiStatus(cluster.cluster_id),
                fetchResult: () => api.aiAnalysis(cluster.cluster_id),
              });
              setAiResult(result); setAiState('done');
            }
            catch (e) { setAiError(e); setAiState('error'); }
          }}
        >
          {aiState === 'loading' ? 'Analyzing… (1–2 min)' : aiState === 'done' ? 'Re-run analysis' : '✨ Analyze with Glean'}
        </button>
      </div>
      {aiState === 'loading' && (
        <p className="ai__note"><span className="ai-spin" aria-hidden="true" /> Glean is reading the evidence, our code and runbooks…</p>
      )}
      {aiState === 'error' && (
        <p className={`ai__note ${aiError?.status === 503 ? '' : 'ai__note--error'}`}>
          {aiError?.status === 503
            ? 'AI analysis is not configured — set GLEAN_TOKEN on the API (Glean admin → Client API Tokens).'
            : `Analysis failed: ${aiError?.message}`}
        </p>
      )}
      {aiState === 'done' && aiResult && (
        <div className="ai">
          <div className="ai__md">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{aiResult.analysis}</ReactMarkdown>
          </div>
          {aiResult.citations?.length > 0 && (
            <ul className="ai__citations">
              {aiResult.citations.map((c) => (
                <li key={c.url}><a href={c.url} target="_blank" rel="noreferrer">{c.title}</a></li>
              ))}
            </ul>
          )}
          <p className="ai__meta">
            Generated by Glean{aiResult.cached ? ' (cached)' : ''}
            {aiResult.citations?.length > 0 && <> · grounded in {aiResult.citations.length} internal source{aiResult.citations.length === 1 ? '' : 's'}</>}
            {' '}· verify before acting — the PR prompt below stays the human-in-the-loop path
          </p>
        </div>
      )}

      {/* Collapsed by default — a wall of prompt text on every cluster reads as
          noise. Copy works without expanding; Show is for the humans who want to
          read or tweak before pasting. */}
      <div className="prompt__head">
        <h3 className="detail__h3">PR prompt — paste into Claude/Cursor with the AUTE skills</h3>
        <span className="prompt__actions">
          <CopyButton text={cluster.pr_prompt || ''} />
          <button
            type="button"
            className="ghost-btn"
            aria-expanded={showPrompt}
            onClick={() => setShowPrompt((v) => !v)}
          >
            {showPrompt ? 'Hide' : 'Show'}
          </button>
        </span>
      </div>
      {showPrompt && (
        <textarea
          className="prompt"
          readOnly
          value={cluster.pr_prompt || ''}
          rows={Math.min(24, (cluster.pr_prompt || '').split('\n').length + 1)}
          onFocus={(e) => e.target.select()}
          aria-label="Copy-ready PR prompt"
        />
      )}

    </div>
  );
}
